import React, { useEffect, useRef, useState } from "react";
import { Dayjs } from "dayjs";
import { useTranslation } from "react-i18next";
import { DatePicker, Form } from "antd";
import InputLabel from "@companion-core/web/src/Components/InputLabel";
import { FontIcon } from "@companion-core/shared/app/Components/FontIcon";
import { JPInputError } from "@companion-core/shared/app/Components/Inputs/JPInputError";
import { useI18nStore } from "@companion-core/shared/app/Store/i18nStore";
import getValidation from "@companion-core/shared/app/Utils/validators";
import {
  dateToLocalizedString,
  getLocalizedDateFormat,
  getDate,
  isBetweenDates,
  localizedStringToDate,
} from "@companion-core/shared/app/Utils/datetime";
import { JPInputDateRangeProps } from "./jpInputDateRange.model";
import "@companion-core/web/src/Assets/Styles/scss/input.scss";

/**
 * DateRange component with 2 date inputs (departure/return) that communicate with each other.
 * The return date is always >= the departure date.
 *  If the departure date is today, the return date cannot be earlier than today.
 */
export const JPInputDateRange: React.FC<JPInputDateRangeProps> = ({
  name = "date-range",
  required = false,
  value,
  minimumDate = "now",
  maximumDate = "",
  startDateLabel,
  endDateLabel,
  icon,
  validators,
  testID,
  startDatePayloadPath,
  endDatePayloadPath,
  disabled = false,
  setFieldErrors,
  onValueChange,
  registerValidation,
}: JPInputDateRangeProps) => {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const [error, setError] = useState<string>("");
  const [startDate, setStartDate] = useState<Dayjs | null>(
    value?.start_date ? localizedStringToDate(value.start_date) : null
  );
  const [endDate, setEndDate] = useState<Dayjs | null>(
    value?.end_date ? localizedStringToDate(value.end_date) : null
  );
  const inputValueRef = useRef({ startDate, endDate });
  const dateRangeContainerRef = useRef<HTMLDivElement>(null);

  const [localizedFormat, setLocalizedFormat] = useState<string>("MM/DD/YYYY");

  const { locale: lang, antdLocale } = useI18nStore((state) => ({
    locale: state.locale,
    antdLocale: state.antdLocale,
  }));

  useEffect(() => {
    registerValidation?.(name, validateInput);
  }, []);

  useEffect(() => {
    inputValueRef.current = { startDate, endDate };
  }, [startDate, endDate]);

  useEffect(() => {
    setLocalizedFormat(getLocalizedDateFormat());
  }, [lang]);

  const minStartDate = minimumDate ? getDate(minimumDate) : null;
  const maxStartDate = maximumDate ? getDate(maximumDate) : null;

  const disabledStartDate = (d: Dayjs) => isBetweenDates(d, minimumDate, maximumDate);

  const disabledEndDate = (d: Dayjs) => {
    if (!d) return true;
    const minForEnd = startDate || minStartDate;
    if (minForEnd && d.isBefore(minForEnd, "day")) return true;
    if (maximumDate && d.isAfter(getDate(maximumDate), "day")) return true;
    return false;
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    setStartDate(date);
    if (date && endDate && endDate.isBefore(date, "day")) {
      setEndDate(date);
      const endStr = dateToLocalizedString(date);
      if (form && endDatePayloadPath) {
        form.setFieldsValue({ [endDatePayloadPath]: endStr });
      }
    }
    const startStr = date ? dateToLocalizedString(date) : "";
    if (form && startDatePayloadPath) {
      form.setFieldsValue({ [startDatePayloadPath]: startStr });
    }
    notifyValueChange(
      date ? dateToLocalizedString(date) : "",
      date && endDate && !endDate.isBefore(date, "day")
        ? dateToLocalizedString(endDate)
        : date
          ? dateToLocalizedString(date)
          : ""
    );
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    setEndDate(date);
    const endStr = date ? dateToLocalizedString(date) : "";
    if (form && endDatePayloadPath) {
      form.setFieldsValue({ [endDatePayloadPath]: endStr });
    }
    notifyValueChange(startDate ? dateToLocalizedString(startDate) : "", endStr);
  };

  const notifyValueChange = (startStr: string, endStr: string) => {
    if (onValueChange) {
      const summaryValue =
        startStr && endStr
          ? t("criteria.common.date-range.value", {
              startDate: startStr,
              endDate: endStr,
              interpolation: { escapeValue: false },
            })
          : "";
      onValueChange({
        values: { start_date: startStr, end_date: endStr },
        summaryValue,
      });
    }
  };

  const validateInput = () => {
    const { startDate: s, endDate: e } = inputValueRef.current;
    const startStr = s ? dateToLocalizedString(s) : "";
    const endStr = e ? dateToLocalizedString(e) : "";
    let err = "";

    if (required && (!startStr || !endStr)) {
      err = "common.form.input.error.required";
    } else if (startStr && endStr && e && s && e.isBefore(s, "day")) {
      err = "common.form.input.error.dateRange.invalid";
    } else if (validators?.length) {
      const { isValid, errorMessages } = getValidation(
        validators,
        JSON.stringify({ start_date: startStr, end_date: endStr })
      );
      if (!isValid) err = errorMessages[0];
    }
    setError(err);
    setFieldErrors?.(err ? [err] : []);
  };

  const clearStartDate = () => {
    setStartDate(null);
    setEndDate(null);
    if (form) {
      if (startDatePayloadPath) form.setFieldsValue({ [startDatePayloadPath]: "" });
      if (endDatePayloadPath) form.setFieldsValue({ [endDatePayloadPath]: "" });
    }
    notifyValueChange("", "");
    setError("");
  };

  const clearEndDate = () => {
    setEndDate(null);
    if (form && endDatePayloadPath) {
      form.setFieldsValue({ [endDatePayloadPath]: "" });
    }
    notifyValueChange(startDate ? dateToLocalizedString(startDate) : "", "");
  };

  const startInputId = `${name}-start-date`;
  const endInputId = `${name}-end-date`;
  const errorId = `${name}-error`;
  const defaultStartLabel = t("common.form.input.label.departureDate");
  const defaultEndLabel = t("common.form.input.label.returnDate");

  const displayStartLabel = startDateLabel ?? defaultStartLabel;
  const displayEndLabel = endDateLabel ?? defaultEndLabel;
  const missingInputLabel =
    [!startDate ? displayStartLabel : null, !endDate ? displayEndLabel : null]
      .filter(Boolean)
      .join(", ") || undefined;

  useEffect(() => {
    if (!dateRangeContainerRef.current) return;

    const startInput = dateRangeContainerRef.current.querySelector<HTMLInputElement>(
      `#${startInputId}`
    );
    const endInput = dateRangeContainerRef.current.querySelector<HTMLInputElement>(
      `#${endInputId}`
    );
    const dateInputs = [startInput, endInput].filter(Boolean) as HTMLInputElement[];

    dateInputs.forEach((input) => {
      // remove all aria attributes that are not needed (added by ant design)
      input.removeAttribute("aria-haspopup");
      input.removeAttribute("aria-controls");
      input.removeAttribute("aria-activedescendant");
      input.removeAttribute("aria-expanded");
      input.removeAttribute("aria-labelledby");

      if (error) {
        // If there is an error, the error message will be displayed instead of the required message
        input.removeAttribute("aria-required");
      } else {
        input.setAttribute("aria-required", required.toString());
      }
    });
  }, [error, required, startInputId, endInputId, startDate, endDate]);

  return (
    <div className="JPInput JPInputDateRange" ref={dateRangeContainerRef}>
      <div className="JPInputDateRange__inputs">
        <div className="JPInputDateRange__input">
          <InputLabel
            label={displayStartLabel}
            icon={icon}
            htmlFor={startInputId}
            id={`${name}-start-date-label`}
          />
          <DatePicker
            id={startInputId}
            status={error ? "error" : ""}
            data-testid={`${testID}--start`}
            locale={antdLocale?.DatePicker}
            picker="date"
            placeholder={!disabled ? t("common.form.input.placeholder.date") : ""}
            showTime={false}
            value={startDate}
            disabledDate={disabledStartDate}
            format={localizedFormat}
            suffixIcon={false}
            showNow={false}
            allowClear={{
              clearIcon: (
                <FontIcon
                  name="JP-Circle-close-outline"
                  size={24}
                  data-testid={`${testID}--start-clear`}
                  onClick={clearStartDate}
                />
              ),
            }}
            disabled={disabled}
            onChange={handleStartDateChange}
            onBlur={validateInput}
            aria-invalid={!!error}
          />
        </div>
        <div className="JPInputDateRange__input">
          <InputLabel
            label={displayEndLabel}
            icon={icon}
            htmlFor={endInputId}
            id={`${name}-end-date-label`}
          />
          <DatePicker
            id={endInputId}
            status={error ? "error" : ""}
            data-testid={`${testID}--end`}
            locale={antdLocale?.DatePicker}
            picker="date"
            placeholder={!disabled ? t("common.form.input.placeholder.date") : ""}
            showTime={false}
            value={endDate}
            disabledDate={disabledEndDate}
            format={localizedFormat}
            suffixIcon={false}
            showNow={false}
            allowClear={{
              clearIcon: (
                <FontIcon
                  name="JP-Circle-close-outline"
                  size={24}
                  data-testid={`${testID}--end-clear`}
                  onClick={clearEndDate}
                />
              ),
            }}
            disabled={disabled}
            onChange={handleEndDateChange}
            onBlur={validateInput}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
          />
        </div>
      </div>
      {error && (
        <JPInputError inputLabel={missingInputLabel} testID={testID} error={error} id={errorId} />
      )}
    </div>
  );
};
